import React, { useState } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
  DialogContentText,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import colors from "../../../../Constants/colors";
import { subscriptionCardLayout, textfieldLayout } from "../PricingStyles";
import { useSelector } from "react-redux";
import emailjs from "@emailjs/browser";
import { dialogStyles } from "../../../../Constants/dialogStyles";
import { inputStyles } from "../../../../Constants/inputStyles";
import theme from "../../../../Constants/theme";
import {
  primaryButtonStyles,
  secondaryButtonStyles,
} from "../../../../Constants/buttonStyles";

const SubscriptionCard = ({
  title,
  price,
  features,
  buttonText,
  setPlan,
  handleSubscribe,
  plan,
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { organization } = useSelector((state) => state.organization);
  const [open, setOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [formValues, setFormValues] = useState({
    name: user?.user?.first_name + " " + user?.user?.last_name,
    email: user?.user?.email,
    description: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false); // For snackbar
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for snackbar
  const [snackbarSeverity, setSnackbarSeverity] = useState("info"); // Severity of the alert

  // Open snackbar with a message and severity
  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubmitEmail = () => {
    const { name, email, description } = formValues;

    const templateParams = {
      to_name: name,
      to_email: "support@queryloop.ai",
      organization: organization?.name,
      query_type: "Pro Subscription",
      query_description: description,
      email_priority: "Normal",
      from_name: "QueryLoop Support",
      from_email: email,
    };

    emailjs
      .send(
        "service_g5lbm6c", // Replace with your EmailJS service ID
        "template_87ktt1n", // Replace with your EmailJS template ID
        templateParams,
        "0W0QSF4pkfnX1qPwx" // Replace with your EmailJS user ID
      )
      .then(
        (response) => {
          // Show success message
          showSnackbar("Your message has been sent successfully!", "success");
          handleClose(); // Close the dialog
        },
        (err) => {
          // Show error message
          showSnackbar(
            "Error sending your message. Please try again.",
            "error"
          );
        }
      );
  };

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setMousePosition({ x, y });
  };

  const getBackgroundGradient = () => {
    return `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(67, 61, 255, 0.5), ${colors.extremeBlack})`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleButtonClick = () => {
    if (title === "QueryLoop Pro") {
      handleSubscribe("pro");
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setFormValues({
      name: user?.user?.first_name + " " + user?.user?.last_name,
      email: user?.user?.email,
      description: "",
    });
    setOpen(false);
  };

  const isFormValid = () => {
    const { name, email, description } = formValues;
    return name && email && description;
  };

  return (
    <>
      <Card
        sx={{
          ...subscriptionCardLayout,
          background: getBackgroundGradient(),
          transition: "transform 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
            zIndex: 10,
          },
          position: "relative",
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
      >
        <CardContent sx={{ flexGrow: 1, textAlign: "left" }}>
          <Typography
            variant="h5"
            sx={{ color: colors.extremeWhite, fontWeight: "bold" }}
          >
            {title}
          </Typography>
          <Typography
            variant="h6"
            sx={{ margin: "8px 0", color: colors.extremeWhite }}
          >
            {price}
          </Typography>
          {plan.title !== "Starter" &&
            (organization?.subscription_cancellation_status === "active" &&
            plan.title !== "QueryLoop Enterprise" ? (
              <Typography
                sx={{
                  fontSize: "15px",
                  fontFamily: "var(--font-primary)",
                  color: "var(--colorDanger)",
                }}
              >
                {buttonText}
              </Typography>
            ) : (
              <Button
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: colors.primaryBlue,
                  color: colors.extremeWhite,
                  padding: "10px 20px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  "&:hover": {
                    backgroundColor: colors.tertiaryBlue,
                  },
                  ":disabled": {
                    color: colors.extremeWhite,
                  },
                }}
                disabled={title === "Starter"}
                onClick={handleButtonClick}
              >
                {plan === title ? "Subscribed" : buttonText}
              </Button>
            ))}
          <Divider
            sx={{
              backgroundColor: colors.extremeWhite,
              marginBottom: "16px",
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: colors.extremeWhite, textAlign: "left" }}
          >
            <strong>Includes:</strong>
          </Typography>
          <List>
            {features.map((feature, index) => (
              <ListItem key={index} sx={{ padding: "4px 0" }}>
                <ListItemIcon>
                  <Check sx={{ color: colors.primaryBlue }} />
                </ListItemIcon>
                <ListItemText
                  primary={feature}
                  sx={{ color: colors.extremeWhite }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Dialog
        PaperProps={{
          sx: {
            ...dialogStyles.dialogRoot,
          },
        }}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle sx={dialogStyles.dialogTitle}>
          <IconButton onClick={handleClose} sx={dialogStyles.dialogCloseIcon}>
            <Close fontSize="small" />
          </IconButton>
          <Typography sx={dialogStyles.dialogTitleText}>Contact Us</Typography>
        </DialogTitle>

        <DialogContent dividers sx={dialogStyles.dialogContent}>
          <DialogContentText sx={dialogStyles.dialogContentText}>
            Please provide your name, email, and a brief message. We'll get back
            to you shortly.
          </DialogContentText>
          <TextField
            autoFocus
            name="name"
            type="text"
            fullWidth
            variant="outlined"
            value={formValues.name}
            onChange={handleInputChange}
            placeholder="Enter your name"
            sx={inputStyles.textField}
          />
          <TextField
            name="email"
            type="email"
            fullWidth
            variant="outlined"
            value={formValues.email}
            onChange={handleInputChange}
            placeholder="Enter your Email"
            sx={inputStyles.textField}
          />
          <TextField
            name="description"
            type="text"
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            value={formValues.description}
            onChange={handleInputChange}
            placeholder="Type your message here..."
            sx={inputStyles.textField}
            InputLabelProps={{ style: { color: theme.inputs.textColor } }}
          />
        </DialogContent>
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button onClick={handleClose} sx={secondaryButtonStyles}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitEmail}
            sx={primaryButtonStyles}
            disabled={!isFormValid()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000} // Closes after 6 seconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} // Position of the snackbar
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SubscriptionCard;
